import {useCallback, useEffect, useRef, useState} from 'react';
import {subscribeToUserDrafts} from '../../../firebase/firestore/reports';
import {getIcrMapByNumbers, resolveReportLinkedIcrNumber} from '../../../firebase/firestore/icr';

function resolveIcrNumber(report) {
    return resolveReportLinkedIcrNumber(report);
}

export function enrichDraftsWithIcr(reports, icrByNumber) {
    return (reports || []).map((report) => {
        const icrNumber = resolveIcrNumber(report);
        const icr = icrNumber ? icrByNumber[icrNumber] : null;
        return {
            ...report,
            icrNumber,
            icrSubject: icr?.subject || null,
        };
    });
}

/**
 * Custom hook to subscribe to user draft reports.
 * Drafts are reports where status !== 'approved'.
 * Exposes a `refresh` callback to force a re-fetch (e.g. after a delete).
 *
 * @param {string} userId - The user ID to fetch reports for
 * @returns {Object} - { reports, loading, error, refresh }
 */
export const useUserDrafts = (userId) => {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);
    const cancelRef = useRef(false);

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        cancelRef.current = false;

        const unsubscribe = subscribeToUserDrafts(
            userId,
            async (data) => {
                if (cancelRef.current) return;
                try {
                    const rows = Array.isArray(data) ? data : [];
                    const icrNumbers = rows.map(resolveIcrNumber).filter(Boolean);
                    const icrByNumber = icrNumbers.length
                        ? await getIcrMapByNumbers(icrNumbers)
                        : {};
                    if (!cancelRef.current) {
                        setReports(enrichDraftsWithIcr(rows, icrByNumber));
                        setError(null);
                        setLoading(false);
                    }
                } catch (err) {
                    if (!cancelRef.current) {
                        console.error('Failed to enrich draft ICR subjects', err);
                        setReports(Array.isArray(data) ? data : []);
                        setError(err);
                        setLoading(false);
                    }
                }
            },
            (err) => {
                if (!cancelRef.current) {
                    console.error('Failed to subscribe to draft reports', err);
                    setError(err);
                    setLoading(false);
                }
            }
        );

        return () => {
            cancelRef.current = true;
            try {
                unsubscribe && unsubscribe();
            } catch (e) {
                // Cleanup error - safe to ignore
            }
        };
    }, [userId, tick]);

    const refresh = useCallback(() => setTick((t) => t + 1), []);

    return {reports, loading, error, refresh};
};
